import { TokenKind } from '../../json/tokens';
import { firstCharOf, scanStructure, textOf } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/** Delimiters that belong to another rule: `'a'`, `"a"`, `“a”`, `‘a’`. */
const FOREIGN_DELIMITERS = new Set(["'", '"', '“', '‘']);

/**
 * Put quotes around a bare property name: `{a:1}`, `{a-b:1}`, `{true:1}`.
 *
 * The position comes from the container scan, so "key" means *the grammar is reading a
 * property name here*, not "an identifier followed by a colon somewhere in the text". The
 * legacy `/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g` matched the latter and therefore rewrote the
 * inside of `{"a":"{b: 1}","c":2,}` into unparseable garbage (recon §3.3) while missing
 * `a-b` entirely, since `-` is not an identifier character. Reading the token stream fixes
 * both: any token the scanner handed us in key position is quoted, whatever it contains.
 *
 * `JSON.stringify` does the quoting, so a key holding a quote, a backslash or a control
 * character comes out correctly escaped instead of producing a second broken document.
 */
export const quoteUnquotedKeyRule: RepairRule = {
  id: 'quote-unquoted-key',
  confidence: 'safe',
  priority: 30,
  resolves: ['key/unquoted'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const proposals: RepairProposal[] = [];

    for (const token of scanStructure(ctx.source, ctx.tokens).namedKeys) {
      if (token.kind === TokenKind.String) continue;
      // A quoted-but-foreign key (`'a'`, `“a”`) is re-quoted by its own rule, which knows
      // how to decode the literal. Wrapping it here would produce `"'a'"`.
      if (FOREIGN_DELIMITERS.has(firstCharOf(ctx.source, token))) continue;

      const name = textOf(ctx.source, token);
      if (name.length === 0) continue;

      proposals.push({
        ruleId: 'quote-unquoted-key',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.from, to: token.to, insert: JSON.stringify(name) }],
        labelCode: 'fix.quote-unquoted-key',
        labelParams: { name },
        resolves: 'key/unquoted',
      });
    }

    return proposals;
  },
};
